import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import { trackEvent, EVENTS } from "../lib/analytics";
import { enqueueBadgeUnlock } from "../components/AchievementUnlockedPopup";

// AccessContext exposes the user's *effective* premium state:
// - is_premium (paid premium currently active)
// - in_trial (within 10-day welcome window)
// - effective_premium = is_premium || in_trial
// + days_left counters for trial and paid premium
//
// One single source of truth, fetched once at login + on focus. No polling.
const AccessContext = createContext({
  access: null,
  effectivePremium: false,
  refresh: () => {},
});

// Per-user localStorage flag so `trial_started` only fires once per browser.
const TRIAL_FIRED_KEY = "syllox_trial_started_fired_v1";

export const AccessProvider = ({ children }) => {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setAccess(null);
      return null;
    }
    try {
      const { data } = await api.get("/me/access");
      setAccess(data);
      return data;
    } catch {
      setAccess(null);
      return null;
    }
  }, [user]);

  // (Re)fetch when the authenticated user changes — i.e. after login,
  // after profile updates that affect premium (e.g. /premium/verify), and
  // when navigating back to the tab after a long gap (visibility change).
  useEffect(() => { refresh(); }, [refresh]);

  useEffect(() => {
    const onVis = () => { if (!document.hidden) refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [refresh]);

  // Fire `trial_started` exactly once per browser when this user first lands
  // in the 10-day welcome window. Gated by a localStorage flag scoped to the
  // user_id so the same browser used by a different user re-fires correctly.
  useEffect(() => {
    if (!user?.user_id || !access?.in_trial) return;
    try {
      const seen = JSON.parse(localStorage.getItem(TRIAL_FIRED_KEY) || "{}");
      if (seen[user.user_id]) return;
      trackEvent(EVENTS.TRIAL_STARTED, {
        trial_days_left: access.trial_days_left,
      });
      seen[user.user_id] = true;
      localStorage.setItem(TRIAL_FIRED_KEY, JSON.stringify(seen));
    } catch {
      // localStorage failure (private-mode iOS) — degrade silently
    }
  }, [user?.user_id, access?.in_trial, access?.trial_days_left]);

  // Drain pending badge celebrations (backfilled + real-time earns that
  // never got mark-shown) and enqueue them into the popup queue. The drain
  // endpoint is now READ-only — mark-shown (fired when each popup closes)
  // is the sole path that clears server-side pending. This gives us
  // exactly-once celebration across devices / sessions / refreshes.
  //
  // Client-side dedupe inside `enqueueBadgeUnlock` prevents a double-show
  // if the drain fires again before mark-shown completes.
  useEffect(() => {
    if (!user?.user_id || !access?.effective_premium) return;
    api.post("/achievements/announcements/drain")
      .then((r) => {
        const pending = r.data?.pending || [];
        if (pending.length > 0) enqueueBadgeUnlock(pending);
      })
      .catch(() => { /* swallow — non-critical surface */ });
  }, [user?.user_id, access?.effective_premium]);

  const value = useMemo(() => ({
    access,
    // Public setter so callers with a freshly-verified server response
    // (e.g. /premium/verify) can update the cache atomically without a
    // second GET /me/access round-trip. Eliminates the flash where the
    // trial badge briefly reappears after payment.
    setAccess,
    effectivePremium: !!access?.effective_premium,
    refresh,
  }), [access, refresh]);

  return <AccessContext.Provider value={value}>{children}</AccessContext.Provider>;
};

export const useAccess = () => useContext(AccessContext);
